import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Card, IconButton, useToast } from "@totalsoft/rocket-ui";
import { Box, Grid, CircularProgress } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { useMutation, useApolloClient } from "@apollo/client";
import { RETRY_PARTNER_SYNCHRONIZATION } from "../mutations";
import { useSimpleError } from "hooks/apolloWrappers";

const PartnerSynchronizationListItem = ({ data }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const showError = useSimpleError();
  const showToast = useToast();
  const [synchronizationPending, setSynchronizationPending] = useState(false);

  const [retryPartnerSynchronization] = useMutation(RETRY_PARTNER_SYNCHRONIZATION, {
    onError: error => {
      setSynchronizationPending(false);
      showError(error);
    },
    onCompleted: async data => {
      if (data.retryPartnerSynchronization.synchronizationCompleted)
        showToast(t("PartnerSynchronization.FinishSynchronizationRetry"), "success", { autoClose: 3000 });
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "partnerSynchronizationQueue" });
    }
  });

  const handleRetrySynchronization = useCallback(() => {
    setSynchronizationPending(true);
    retryPartnerSynchronization({
      variables: { partnerId: data.partnerId }
    });
  }, [data.partnerId, retryPartnerSynchronization]);

  return (
    <Card flexWrap="nowrap" alignItems="center" position="relative">
      <Grid container px={1}>
        <Grid item container xs={11}>
          <Grid item xs={12} md={4} lg={3} id={"partnerSynchronization_" + data.id + "_partnerName"}>
            <strong> {t("PartnerSynchronization.Fields.PartnerName")}</strong>: <span>{data.partnerName}</span>
          </Grid>
          <Grid item xs={12} md={4} lg={3} id={"partnerSynchronization_" + data.id + "_uniqueId"}>
            <strong> {t("PartnerSynchronization.Fields.UniqueId")}</strong>: <span>{data.uniqueId}</span>
          </Grid>
          <Grid item xs={12} md={4} lg={3} id={"partnerSynchronization_" + data.id + "_status"}>
            <strong> {t("PartnerSynchronization.Fields.Status")}</strong>: <span>{data.status}</span>
          </Grid>
          <Grid item xs={12} lg={3} id={"partnerSynchronization_" + data.id + "_errorMessage"}>
            <strong> {t("PartnerSynchronization.Fields.Message")}</strong>: <span>{data.errorMessage}</span>
          </Grid>
        </Grid>
        <Grid container item width="auto" flexGrow={1} justifyContent="end" sx={{ padding: 0 }}>
          <CircularIconButton
            success={data.completed}
            loading={synchronizationPending}
            handleClick={handleRetrySynchronization}
            disabled={data.completed}
            tooltip={data.completed ? t("Tooltips.Completed") : t("Tooltips.Retry")}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

const CircularIconButton = ({ handleClick, success, loading, disabled, tooltip }) => {
  return (
    <Box sx={{ position: "relative" }}>
      <IconButton
        aria-label="save"
        color={"primary"}
        variant="text"
        tooltip={tooltip}
        onClick={handleClick}
        disabled={disabled}
      >
        {success ? <CheckIcon fontSize="small" /> : <ReplayIcon fontSize="small" />}
      </IconButton>
      {loading && (
        <CircularProgress
          size={55}
          color={"success"}
          sx={{
            position: "absolute",
            top: -4,
            left: -4,
            zIndex: 1
          }}
        />
      )}
    </Box>
  );
};

CircularIconButton.propTypes = {
  handleClick: PropTypes.func,
  disabled: PropTypes.bool,
  success: PropTypes.bool,
  loading: PropTypes.bool,
  tooltip: PropTypes.string
};

PartnerSynchronizationListItem.propTypes = {
  data: PropTypes.object.isRequired
};

export default PartnerSynchronizationListItem;
